<template>
  <div class="luggage-selector" :class="{ disabled: !storeId }">
    <div class="luggage-header">
      <div>
        <div class="field-label">{{ $t('luggage') || 'Luggage Items' }}</div>
        <p class="sub-label">Manage your tracking inventory</p>
      </div>
      <button type="button" class="btn-add" :disabled="!storeId" @click="addItem">+ {{ $t('addItem').replace('+ ', '') }}</button>
    </div>

    <p v-if="!storeId" class="hint">{{ $t('selectStoreFirst') }}</p>
    <p v-if="storeId && !sizes.length" class="hint">{{ $t('noPackages') }}</p>

    <TransitionGroup name="item-fade" tag="div" class="item-list">
      <div v-for="(item, idx) in items" :key="item._key" class="luggage-item">
        <button type="button" class="btn-remove" @click="removeItem(idx)" aria-label="Remove item">&times;</button>

        <div class="group-label">ITEM CATEGORY</div>
        <div class="size-wrap">
          <label class="sr-only" :for="'size-' + idx">Size</label>
          <select
            :id="'size-' + idx"
            class="field-input size-select"
            :value="item.size"
            @change="updateSize(idx, $event.target.value)"
          >
            <option value="" disabled>{{ $t('luggageSizePlaceholder') }}</option>
            <option v-for="s in sizes" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </div>

        <div class="meta-row">
          <div class="qty-box">
            <div class="group-label">QUANTITY</div>
            <div class="qty-controls">
              <button type="button" class="qty-btn" @click="decrementQuantity(idx)">−</button>
              <span class="qty-value">{{ formatQty(item.quantity) }}</span>
              <button type="button" class="qty-btn primary" @click="incrementQuantity(idx)">+</button>
            </div>
          </div>

          <div class="price-box">
            <div class="group-label">UNIT PRICE</div>
            <div class="item-fee">${{ feeForSize(item.size) }}</div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
let keySeq = 0

export default {
  name: 'LuggageSelector',
  props: {
    storeId: { type: Number, default: null },
    value: { type: Array, default: () => [] },
    feeOptions: { type: Array, default: () => [] },
    dropOffDate: { type: String, default: '' },
    pickUpDate: { type: String, default: '' }
  },
  computed: {
    sizes () {
      return this.feeOptions.map(function (fee) {
        return { value: fee.size, label: fee.size }
      })
    },
    items () {
      return this.value.map(function (i) {
        var next = Object.assign({}, i)
        next._key = i._key || ++keySeq
        next.quantity = Number(next.quantity) > 0 ? Number(next.quantity) : 1
        return next
      })
    }
  },
  methods: {
    formatQty (qty) {
      const n = Math.max(1, parseInt(qty, 10) || 1)
      return String(n).padStart(2, '0')
    },
    feeForSize (size) {
      var fee = this.feeOptions.find(function (item) { return item.size === size })
      if (!fee || !fee.rules || !fee.rules.length) return '—'

      const dropOff = new Date(this.dropOffDate)
      const pickUp = new Date(this.pickUpDate)
      const validDates = !isNaN(dropOff.getTime()) && !isNaN(pickUp.getTime())
      const diffMinutes = validDates ? Math.max(1, Math.ceil((pickUp - dropOff) / 60000)) : 1
      const hours = Math.max(1, Math.ceil(diffMinutes / 60))
      const days = Math.max(1, Math.ceil(diffMinutes / 1440))
      const rule = this.findRuleByHours(fee.rules, hours)
      const price = this.calculateStorageFee(fee.rules, hours, days, rule)
      const discount = rule ? this.normalizeDiscount(rule.discount) : 1
      return price == null ? '—' : Number(price * discount).toFixed(2)
    },
    calculateStorageFee (rules, timeHours, days, matchedRule) {
      if (this.usesLegacyStoragePricing()) {
        return this.calculateLegacyStorageFeeWithRules(rules, timeHours, days, matchedRule)
      }

      return this.calculateNewStorageFeeWithRules(rules, timeHours, matchedRule)
    },
    calculateLegacyStorageFeeWithRules (rules, timeHours, days, matchedRule) {
      const hours = Math.max(1, Math.ceil(timeHours))
      const rule = matchedRule || this.findRuleByHours(rules, hours)
      if (!rule) return null

      if (this.isDayRule(rule)) {
        return (Number(rule.fee) || 0) * Math.max(1, days)
      }

      return Number(rule.fee) || 0
    },
    calculateNewStorageFeeWithRules (rules, timeHours, matchedRule) {
      const hours = Math.max(1, Math.ceil(timeHours))
      const rule = matchedRule || this.findRuleByHours(rules, hours)
      if (!rule) return null

      if (this.isDayRule(rule)) {
        const hourRule12To24 = this.getHour12To24Rule(rules)
        if (!hourRule12To24) return null
        const extraSegments = Math.ceil((hours - 24) / 12)
        return (Number(hourRule12To24.fee) || 0) + extraSegments * (Number(rule.fee) || 0)
      }

      return Number(rule.fee) || 0
    },
    findRuleByHours (rules, timeHours) {
      const hours = Math.max(1, Math.ceil(timeHours))
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i]
        const range = this.normalizeRuleRange(rule)
        if (hours > range.start && (range.end === 0 || hours <= range.end)) {
          return rule
        }
      }

      if (hours > 24) {
        return this.getDayRule(rules) || rules[rules.length - 1]
      }

      return null
    },
    normalizeRuleRange (rule) {
      let start = Number(rule.start || 0)
      let end = Number(rule.end || 0)
      if (this.isDayRule(rule)) {
        start = start * 24
        end = end * 24
      }
      return { start: start, end: end }
    },
    getDayRule (rules) {
      return rules.find(rule => this.isDayRule(rule))
    },
    getHour12To24Rule (rules) {
      let lastHourRule = null
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i]
        if (!this.isHourRule(rule)) continue
        lastHourRule = rule
        if (Number(rule.end) === 24) return rule
      }
      return lastHourRule
    },
    isDayRule (rule) {
      return rule && String(rule.type).toLowerCase() === 'day'
    },
    isHourRule (rule) {
      return rule && String(rule.type).toLowerCase() === 'hour'
    },
    normalizeDiscount (discount) {
      return discount == null || discount === '' ? 1 : Number(discount)
    },
    usesLegacyStoragePricing () {
      return false
    },
    emitUpdatedItem (idx, updater) {
      const next = this.value.map(function (item, i) {
        if (i !== idx) return item
        var updated = Object.assign({}, item)
        updater(updated)
        updated.quantity = Math.max(1, parseInt(updated.quantity, 10) || 1)
        return updated
      })
      this.$emit('change', next)
    },
    addItem () {
      if (!this.sizes.length) return
      const next = this.value.slice()
      next.push({ size: '', quantity: 1, _key: ++keySeq })
      this.$emit('change', next)
    },
    removeItem (idx) {
      const next = this.value.filter((_, i) => i !== idx)
      this.$emit('change', next)
    },
    updateSize (idx, size) {
      this.emitUpdatedItem(idx, function (updated) {
        updated.size = size
      })
    },
    incrementQuantity (idx) {
      this.emitUpdatedItem(idx, function (updated) {
        updated.quantity = (parseInt(updated.quantity, 10) || 1) + 1
      })
    },
    decrementQuantity (idx) {
      this.emitUpdatedItem(idx, function (updated) {
        updated.quantity = Math.max(1, (parseInt(updated.quantity, 10) || 1) - 1)
      })
    }
  }
}
</script>

<style scoped>
.luggage-selector { margin-bottom: 20px; }
.luggage-selector.disabled { opacity: .55; pointer-events: none; }

.luggage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.field-label { font-size: 15px; font-weight: 700; color: #1e336f; }
.sub-label { font-size: 12px; color: #6b7280; margin-top: 2px; }

.btn-add {
  min-height: 40px;
  border: none;
  border-radius: 10px;
  background: #1e4db7;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 0 16px;
  cursor: pointer;
}
.btn-add:disabled { opacity: .45; cursor: default; }

.hint { font-size: 13px; color: #9ca3af; margin-top: 4px; }
.item-list { display: flex; flex-direction: column; gap: 10px; }

.luggage-item {
  position: relative;
  background: #f3f4f8;
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  padding: 12px;
}

.btn-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #c1c6d6;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.group-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .4px;
  color: #4b5563;
  margin-bottom: 8px;
}

.size-wrap { position: relative; margin-bottom: 12px; }
.size-select {
  width: 100%;
  height: 46px;
  border: 1px solid #d9deea;
  border-radius: 8px;
  background: #dfe2eb;
  color: #222b3d;
  font-size: 14px;
  font-weight: 500;
  padding: 0 38px 0 14px;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
}
.size-wrap:after {
  content: '▼';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8797;
  pointer-events: none;
  font-size: 11px;
}

.meta-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
}

.qty-box { flex: 1; }
.qty-controls { display: inline-flex; align-items: center; gap: 8px; }
.qty-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9deea;
  border-radius: 6px;
  background: #eef1f8;
  color: #27448d;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.qty-btn.primary {
  background: #1e4db7;
  color: #fff;
}
.qty-value {
  min-width: 28px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.price-box { text-align: right; min-width: 96px; }
.item-fee {
  font-size: 24px;
  font-weight: 700;
  color: #1e4db7;
  line-height: 1.1;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}

.item-fade-enter-active, .item-fade-leave-active { transition: all .25s ease; }
.item-fade-enter, .item-fade-leave-to { opacity: 0; transform: translateY(-8px); }

@media (max-width: 640px) {
  .field-label { font-size: 15px; }
  .btn-add { font-size: 13px; }
  .size-select { font-size: 14px; height: 44px; }
  .meta-row { align-items: center; }
  .qty-btn { width: 32px; height: 32px; font-size: 18px; }
  .qty-value { font-size: 15px; }
  .item-fee { font-size: 20px; }
}
</style>



// WEBPACK FOOTER //
// src/components/LuggageSelector.vue